import { styled, css } from 'next-yak'
import { BREAKPOINTS, COLORS, styles as S, ZINDEX } from '@ulule/owl-kit-components/next'

import { EDIT_MODE_SMALL_LAPTOP } from './constants'

export const SIDEBAR_MAX_WIDTH = '395px'

export const Content = styled.div<{ $isEditMode?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 auto;
  max-width: 760px;
`

const mainBreakpointStyle = css`
  padding: 60px 0;
  box-sizing: border-box;
  margin: 0 auto;
  width: 100%;
`

export const Main = styled.div<{ $isEditMode?: boolean }>`
  padding: 30px 0;
  border-bottom: 1px solid ${COLORS.PRIMARY_SAND_200};

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_SMALL_LAPTOP} {
          ${mainBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${mainBreakpointStyle};
        }
      `
    }
  }}
`

const wrapperBreakpointStyle = css`
  box-sizing: content-box;
  display: flex;
  margin: 0 auto;
  max-width: 1200px;
`

export const Wrapper = styled.div<{ $isEditMode?: boolean }>`
  padding: 0 10px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_SMALL_LAPTOP} {
          ${wrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${wrapperBreakpointStyle};
        }
      `
    }
  }}
`

export const OnlyDesktopShow = styled.div<{ $isEditMode: boolean }>`
  display: none;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_SMALL_LAPTOP} {
          display: initial;
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          display: initial;
        }
      `
    }
  }}
`

export const OnlyMobileShow = styled.div`
  margin-left: -10px;
  margin-right: -10px;

  @media screen and ${BREAKPOINTS.LAPTOP} {
    display: none;
  }
`

export const StickyMobileNav = styled(OnlyMobileShow)`
  background-color: red;
  bottom: 0;
  display: block;
  left: 0;
  position: fixed;
  width: 100%;
`

const sidebarBreakpointStyle = css<{ $isEditMode: boolean }>`
  margin-top: 0;
  width: ${({ $isEditMode }) => ($isEditMode ? 'fit-content' : SIDEBAR_MAX_WIDTH)};
  min-width: 336px;
  max-width: ${({ $isEditMode }) => ($isEditMode ? '336px' : SIDEBAR_MAX_WIDTH)};
  margin-left: 45px;
`

export const Sidebar = styled.div<{ $isEditMode: boolean }>`
  margin-top: 40px;

  & > *:not(:last-child) {
    margin-bottom: 38px;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_SMALL_LAPTOP} {
          ${sidebarBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${sidebarBreakpointStyle};
        }
      `
    }
  }}

  @media screen and ${BREAKPOINTS.LAPTOP_L} {
    width: ${SIDEBAR_MAX_WIDTH};
    max-width: ${SIDEBAR_MAX_WIDTH};
    flex: none;
  }
`

export const SidebarHeading = styled.div`
  margin-bottom: 30px;

  ${S.heading.M} {
    margin-bottom: 13px;
  }
`

export const SidebarItem = styled.div<{ $forceBorder?: boolean }>`
  border-bottom: 1px solid ${COLORS.PRIMARY_SAND_200};
  margin-bottom: 28px;
  padding-bottom: 30px;
  position: relative;

  &:last-child {
    border-bottom-width: ${({ $forceBorder }) => ($forceBorder ? '1px' : '0px')};
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    margin: 0px;
    margin-bottom: 28px;
  }
`

// Backer and Sponsor blocks
export const BackerContainer = styled.div<{ $variant?: string }>`
  display: flex;
  padding: 15px;
  align-items: center;
  background-color: ${(props) => (props.$variant === 'sponsor' ? COLORS.PRIMARY_SAND_100 : COLORS.PRIMARY_GREY_000)};
  box-shadow: 0 0 0 1px ${COLORS.PRIMARY_SAND_200};

  @media screen and ${BREAKPOINTS.LAPTOP} {
    align-content: center;
  }

  ${(props) =>
    props.$variant === 'light' &&
    css`
      @media screen and ${BREAKPOINTS.LAPTOP} {
        align-items: start;
        align-content: start;
        padding: 10px 0;
        box-shadow: none;
      }
    `}
`

export const BackerCommentWrapper = styled(S.copy.S.regular)`
  background-color: ${COLORS.PRIMARY_SAND_100};
  border-radius: 0px 4px 4px 4px;
  margin-left: 20px;
  padding: 10px 20px;
  position: relative;
  width: fit-content;

  &::before {
    content: '';
    background-color: ${COLORS.PRIMARY_SAND_100};
    clip-path: polygon(0 0, 100% 0, 100% 100%);
    @supports (clip-path: path('M 0 0')) {
      clip-path: path('M 20 0 L 20 20 L 4 4 Q 0 0 5 0 L 20 0');
    }
    height: 20px;
    left: -19px;
    position: absolute;
    top: 0%;
    width: 20px;
  }
`

export const Avatar = styled.a<{ $variant?: string }>`
  margin-right: 10px;

  ${S.image.Avatar} {
    width: 36px;
    height: 36px;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    ${S.image.Avatar} {
      width: 52px;
      height: 52px;
    }
  }

  ${(props) =>
    props.$variant === 'light' &&
    css`
      @media screen and ${BREAKPOINTS.LAPTOP} {
        ${S.image.Avatar} {
          width: 36px;
          height: 36px;
        }
      }
    `}
`

export const BackerInfo = styled.div<{ $variant?: string }>`
  flex-grow: 1;
  display: block;

  ${(props) =>
    props.$variant === 'sponsor' &&
    css`
      justify-content: space-between;
      align-items: center;
      align-content: center;
    `}
  a {
    text-decoration: none;
  }

  br {
    display: block;
  }

  ${S.copy.M.light} {
    display: inline-block;
  }

  ${S.tag.RoleTag}, ${S.copy.M.light} {
    margin-right: 5px;
  }

  ${S.copy.M.light}[href] {
    color: ${COLORS.PRIMARY_BLUE_500};
    transition: 0.15s ease;

    &:hover {
      color: ${COLORS.TEXT_LINK_HOVER};
    }
  }

  ${S.heading.XXXXXXS} {
    color: ${COLORS.TEXT_SECONDARY};
    display: block;
    margin-top: 5px;
    margin-right: 10px;
    text-transform: uppercase;
  }

  @media screen and ${BREAKPOINTS.MOBILE_L} {
    display: flex;
    justify-content: space-between;
    align-items: center;

    ${S.heading.XS} {
      font-size: 19px;
    }

    br {
      display: none;
    }

    ${S.heading.XXXXXXS} {
      flex: none;
      display: inline-block;
      margin-top: 0px;
    }
  }

  ${(props) =>
    props.$variant === 'light' &&
    css`
      align-items: start;
      align-content: start;

      @media screen and ${BREAKPOINTS.LAPTOP} {
        margin-top: 6px;

        br {
          display: block;
        }

        ${S.heading.XXXXXXS} {
          display: block;
          margin-top: 5px;
        }
      }
    `}
`

export const Blurred = styled.div`
  display: flex;
  filter: blur(8px);
  position: relative;

  &::before {
    background-color: ${COLORS.PRIMARY_SAND_200};
    border-radius: 6px;
    content: '';
    height: 20px;
    position: absolute;
    width: 60px;
    z-index: ${ZINDEX.BELOW};
  }
`
